import { useEffect } from "react";

const REACH = 40; // px around an element where it starts pulling toward the mouse
const STRENGTH = 0.3; // how far it follows the mouse (0–1)
const MAX_SHIFT = 8; // px

/*
 * "Magnetic" buttons: any element with a data-magnetic attribute is gently
 * pulled toward the mouse when it comes close, and eases back when it leaves.
 * Uses the CSS `translate` property, so it doesn't clash with an element's own
 * `transform`. Mouse/trackpad only; off when "reduce motion" is turned on.
 */
export default function useMagnetic() {
  useEffect(() => {
    const finePointer = window.matchMedia?.("(hover: hover) and (pointer: fine)").matches;
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (!finePointer || reduceMotion) return undefined;

    let frame = null;
    let mouse = null;

    const update = () => {
      frame = null;
      document.querySelectorAll("[data-magnetic]").forEach((element) => {
        const box = element.getBoundingClientRect();
        const dx = mouse.x - (box.left + box.width / 2);
        const dy = mouse.y - (box.top + box.height / 2);
        const near =
          Math.abs(dx) < box.width / 2 + REACH && Math.abs(dy) < box.height / 2 + REACH;
        const clamp = (value) => Math.max(-MAX_SHIFT, Math.min(MAX_SHIFT, value * STRENGTH));
        element.style.translate = near ? `${clamp(dx)}px ${clamp(dy)}px` : "";
      });
    };

    const onMove = (event) => {
      mouse = { x: event.clientX, y: event.clientY };
      if (!frame) frame = requestAnimationFrame(update);
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("mousemove", onMove);
      document.querySelectorAll("[data-magnetic]").forEach((element) => {
        element.style.translate = "";
      });
    };
  }, []);
}
